import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  setPersistence,
  browserLocalPersistence,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updatePassword,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc, terminate, clearIndexedDbPersistence } from 'firebase/firestore';
import { httpsCallable } from './firebase/authReadyCallable';
import { auth, db, functions } from '../config/firebase';
import type { User } from '../types/firestore';
import { markPostAuthFailure } from './auth/postAuthFailure';
import { clearSurveyOfflineData } from './survey/clearSurveyOfflineData';

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();

/**
 * Detect browsers where popups are truly blocked and redirect is the ONLY option.
 *
 * Previously this returned true for Android and standalone PWAs, but redirect
 * flow breaks in those contexts due to sessionStorage partitioning — the OAuth
 * handler page can't read the state token stored by the initiating page, which
 * surfaces as "Unable to process request due to missing initial state".
 *
 * Now: only iOS/iPadOS force redirect (Safari blocks popups outright).
 * Android and standalone PWAs use popup-first with redirect fallback
 * (see signInWithGoogle).
 */
export function shouldUseRedirectAuth(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isIPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return isIOS || isIPadOS;
}

// ============================================================================
// AUTHENTICATION
// ============================================================================

/**
 * Sign in with email and password
 */
export async function signIn(email: string, password: string): Promise<FirebaseUser> {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);

  // Update last login timestamp — fire-and-forget so a cold start or network
  // hiccup on mobile doesn't block the login or surface a false error.
  const updateLastLoginFn = httpsCallable(functions, 'shared_updateLastLogin');
  updateLastLoginFn({}).catch((err) => {
    console.warn('updateLastLogin failed (non-blocking):', err);
  });

  return userCredential.user;
}

/**
 * Sign in with Google.
 *
 * Strategy by platform:
 * - **iOS/iPadOS**: redirect flow (Safari blocks popups outright).
 * - **Android / standalone PWA / desktop**: popup-first. If the popup is
 *   blocked (e.g. aggressive browser settings), falls back to redirect.
 *
 * Redirect flow returns `null` because the page navigates away.
 * `completePendingRedirect()` finishes the flow after the user returns.
 *
 * Popup-first on Android avoids the sessionStorage partitioning bug where
 * standalone PWAs and Chrome on Android lose the OAuth state token across
 * the redirect, causing "missing initial state" errors.
 */
export async function signInWithGoogle(): Promise<FirebaseUser | null> {
  // Persist auth in localStorage so the credential survives the OAuth redirect
  // round-trip — including iOS Safari / standalone PWAs where indexedDB (the
  // SDK default) can be evicted or partitioned under ITP. Best-effort: if the
  // browser has no writable local storage, sign-in can still proceed
  // in-memory, so log and continue rather than blocking the user.
  try {
    await setPersistence(auth, browserLocalPersistence);
  } catch (error) {
    console.warn('Could not set local auth persistence; continuing:', error);
  }

  if (shouldUseRedirectAuth()) {
    await signInWithRedirect(auth, googleProvider);
    return null;
  }

  // Popup-first with redirect fallback
  try {
    const userCredential = await signInWithPopup(auth, googleProvider);
    try {
      await ensureUserProfileExists(userCredential.user);
    } catch (postAuthError) {
      // Same shape as the redirect path: the popup has ALREADY authenticated
      // this person, so a blip in the profile read must not be reported to them
      // as "Failed to log in with Google". Tagged here so loginWithGoogle's
      // catch can tell the two apart. (Not caught by the popup-fallback branch
      // below — a Firestore code never matches those popup codes — so the tag
      // survives to the caller.)
      throw markPostAuthFailure(postAuthError);
    }
    return userCredential.user;
  } catch (error: any) {
    const code = error?.code || '';
    // Any signal that the popup isn't viable in this environment → fall back to
    // redirect. Mobile Chrome/WebView can surface a blocked popup as any of
    // these codes, so the fallback must cover all of them (not just
    // popup-blocked) or the user is stranded on the login page with no redirect.
    if (
      code === 'auth/popup-blocked' ||
      code === 'auth/popup-closed-by-user' ||
      code === 'auth/cancelled-popup-request' ||
      code === 'auth/operation-not-supported-in-this-environment'
    ) {
      // Popup failed — fall back to redirect as last resort
      await signInWithRedirect(auth, googleProvider);
      return null;
    }
    throw error;
  }
}

/**
 * Complete a pending OAuth redirect (call once on app mount).
 *
 * Returns the signed-in user if a redirect just completed, or null if no
 * redirect was in flight. Swallows the "missing initial state" error that
 * happens when sessionStorage is partitioned — this leaves the user signed
 * out but lets the app render normally instead of dying on a blank page.
 */
export async function completePendingRedirect(): Promise<FirebaseUser | null> {
  try {
    const result = await getRedirectResult(auth);
    if (!result) return null;
    try {
      await ensureUserProfileExists(result.user);
    } catch (postAuthError) {
      // Everything past getRedirectResult runs with the person ALREADY
      // authenticated, so a failure here is not a failed sign-in. Record that
      // where it is a fact; see auth/postAuthFailure.ts for why this is a tag
      // and not an `auth.currentUser` read, and for the ordering constraint the
      // "No account found" refusal depends on.
      throw markPostAuthFailure(postAuthError);
    }
    return result.user;
  } catch (error: any) {
    const message = String(error?.message || '');
    const code = String(error?.code || '');
    const isPartitionedStorage = message.includes('missing initial state') ||
      code === 'auth/missing-initial-state' ||
      code === 'auth/web-storage-unsupported';
    if (isPartitionedStorage) {
      console.warn('Redirect auth state lost (storage partitioning). User must retry sign-in.', error);
      return null;
    }
    throw error;
  }
}

async function ensureUserProfileExists(user: FirebaseUser): Promise<void> {
  const userDoc = await getDoc(doc(db, 'users', user.uid));
  if (userDoc.exists()) {
    // Fire-and-forget — don't block Google SSO completion on a housekeeping update.
    const updateLastLoginFn = httpsCallable(functions, 'shared_updateLastLogin');
    updateLastLoginFn({}).catch((err) => {
      console.warn('updateLastLogin failed (non-blocking):', err);
    });
  } else {
    // No Firestore profile — this Google account hasn't been invited/onboarded.
    // Sign out immediately to prevent a broken auth state.
    await firebaseSignOut(auth);
    throw new Error('No account found — please contact your administrator for an invitation.');
  }
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<void> {
  await firebaseSignOut(auth);
}

/**
 * Registration hook so a consuming app's OWN offline cache gets wiped
 * alongside the Firestore cache below, WITHOUT this shared file importing
 * that app's code.
 *
 * WHY A HOOK AND NOT A DIRECT IMPORT (#1871 lane-tech-offline-data round 2).
 * `firebase-auth.ts` is shared code, compiled into every client (office, tech,
 * sales, time). The Technician PWA persists its React Query cache to
 * IndexedDB (client-tech/src/config/queryPersistence.ts) so tickets/templates
 * open offline; that store was never wiped on logout or on a different user
 * signing in on the same device — a real shared-tablet leak, tech B could
 * hydrate tech A's tickets, customer data and draft FSRs. The fix belongs
 * where the wipe already happens (clearOfflineCache below), but a bare
 * `import` of client-tech code here would pull PWA-only dependencies
 * (idb-keyval, the persist-client provider) into apps that never persist a
 * React Query cache at all. So the tech app REGISTERS its own clearer at
 * startup (client-tech/src/main.tsx); every other app registers nothing, and
 * `clearOfflineCache()` behaves exactly as it always has for them.
 */
let offlineCacheClearer: (() => Promise<void>) | undefined;

/** Called once, at app startup, by any app that persists its own offline cache. */
export function registerOfflineCacheClearer(fn: () => Promise<void>): void {
  offlineCacheClearer = fn;
}

/** Test-only: undo a registration so suites don't leak state across files. */
export function __resetOfflineCacheClearerForTests(): void {
  if (process.env.NODE_ENV === 'production') return;
  offlineCacheClearer = undefined;
}

/**
 * Clear the on-device Firestore offline cache (IndexedDB).
 *
 * This app now runs `memoryLocalCache()` (see config/firebase.ts), so the live
 * Firestore cache dies with the tab and there is no longer an on-disk copy of the
 * signed-in user's documents to leak on a shared field tablet.
 *
 * The clear is KEPT ANYWAY, and deliberately so: every device that ran an earlier
 * build still has that build's `persistentLocalCache` IndexedDB sitting on disk,
 * full of the surveys / customers / addresses that user fetched. Nothing else ever
 * touches it again — the SDK no longer opens it — so sign-out is the only place
 * that residue gets purged. Removing this call would strand it permanently.
 *
 * `clearIndexedDbPersistence` requires the Firestore instance to be terminated
 * first, after which `db` is unusable — the caller MUST full-reload the app to
 * reinitialize a fresh Firestore instance. Best-effort: it can still reject (for
 * example if another tab from an older build holds the legacy lock); we swallow
 * that rather than block sign-out.
 *
 * Once no client can plausibly still be running a pre-memoryLocalCache build,
 * this becomes dead weight and the terminate + forced reload can go with it.
 */
export async function clearOfflineCache(): Promise<void> {
  // Wipe the survey durable outbox + pending-photo blobs + local note backups
  // FIRST, while `db` is still live and the outgoing user is still authenticated
  // (so a brief best-effort drain can flush their queued work). Same shared-
  // tablet threat model as the Firestore cache wipe below — see
  // clearSurveyOfflineData for the offline-then-logout tradeoff.
  await clearSurveyOfflineData();

  try {
    await terminate(db);
    await clearIndexedDbPersistence(db);
  } catch (err) {
    console.warn('Could not clear Firestore offline cache on sign-out:', err);
  }

  // Tech-app-only in practice (see registerOfflineCacheClearer above) — a
  // no-op for every app that never registers a clearer. MUST complete before
  // this function returns: both call sites (logout(), the shared-device guard
  // in AuthContext) await clearOfflineCache() and then reload/navigate, and
  // that ordering is the whole point — a wipe that raced the reload would
  // still let the next paint hydrate from the stale store.
  if (offlineCacheClearer) {
    try {
      await offlineCacheClearer();
    } catch (err) {
      console.warn('Could not clear the registered offline query cache on sign-out:', err);
    }
  }
}

/**
 * Send password reset email
 */
export async function resetPassword(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

/**
 * Update user password
 */
export async function changePassword(
  user: FirebaseUser,
  newPassword: string
): Promise<void> {
  await updatePassword(user, newPassword);
}

// ============================================================================
// USER PROFILE MANAGEMENT
// ============================================================================

/**
 * Update user profile in Firestore
 */
export async function updateUserProfileData(
  userId: string,
  updates: Partial<Omit<User, 'userId' | 'createdAt'>>
): Promise<void> {
  const updateUserProfileFn = httpsCallable(functions, 'shared_updateUserProfile');
  await updateUserProfileFn({ uid: userId, updates });
}

// ============================================================================
// AUTHENTICATION STATE
// ============================================================================

/**
 * Subscribe to authentication state changes
 * Returns an unsubscribe function
 */
export function onAuthStateChange(
  callback: (user: FirebaseUser | null) => void | Promise<void>
): () => void {
  return onAuthStateChanged(auth, callback);
}

/**
 * Get the current authenticated user
 */
export function getCurrentUser(): FirebaseUser | null {
  return auth.currentUser;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return auth.currentUser !== null;
}

